// supplierTableData.js

import React from "react";
import PropTypes from "prop-types";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { Chip, IconButton, Tooltip } from "@mui/material";
import { Edit, Delete } from "@mui/icons-material";
import { useLanguage } from "context/LanguageContext";
import xmlTypeLabel from "common/utils/xmlTypeLabel";

// Text cell component for displaying simple text
const TextCell = ({ text }) => (
  <MDTypography variant="caption" color="text" fontWeight="medium">
    {text || "-"}
  </MDTypography>
);

TextCell.propTypes = {
  text: PropTypes.string,
};

TextCell.defaultProps = {
  text: "",
};

// Supplier Name component
const Supplier = ({ name }) => (
  <MDTypography display="block" variant="button" fontWeight="medium">
    {name}
  </MDTypography>
);

Supplier.propTypes = {
  name: PropTypes.string.isRequired,
};

// Cell renderer for the supplier name
const SupplierCell = ({ value }) => <Supplier name={value} />;

SupplierCell.propTypes = {
  value: PropTypes.string.isRequired,
};

// Edit button component
const EditButton = ({ supplier, handleEdit }) => {
  const { t } = useLanguage();

  return (
    <MDButton variant="outlined" color="info" size="small" onClick={() => handleEdit(supplier)}>
      {t("edit")}
    </MDButton>
  );
};

EditButton.propTypes = {
  supplier: PropTypes.shape({
    id: PropTypes.string.isRequired, // supplier_id yra string tipo
    name: PropTypes.string.isRequired,
    supplier_registration: PropTypes.string.isRequired,
    primary_warehouse: PropTypes.string,
    primary_group: PropTypes.string,
    pvm_code: PropTypes.string,
    xml_type: PropTypes.string,
  }).isRequired,
  handleEdit: PropTypes.func.isRequired,
};

// Cell renderer for the Actions column
const ActionsCell = ({ row, handleEdit, handleDelete }) => (
  <MDBox>
    <Tooltip title={row.original.has_info ? "Tvarkyti nustatymus" : "Pridėti nustatymus"}>
      <IconButton onClick={() => handleEdit(row.original)}>
        <Edit />
      </IconButton>
    </Tooltip>
    <Tooltip title="Ištrinti tiekėją">
      <IconButton onClick={() => handleDelete(row.original.id)} color="error">
        <Delete />
      </IconButton>
    </Tooltip>
  </MDBox>
);

ActionsCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      has_info: PropTypes.bool,
    }).isRequired,
  }).isRequired,
  handleEdit: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
};

// Cell renderer components with proper PropTypes
const NameCell = ({ cell }) => <TextCell text={cell.value} />;
NameCell.propTypes = {
  cell: PropTypes.shape({
    value: PropTypes.string,
  }).isRequired,
};

const RegistrationCell = ({ cell }) => <TextCell text={cell.value} />;
RegistrationCell.propTypes = {
  cell: PropTypes.shape({
    value: PropTypes.string,
  }).isRequired,
};

const WarehouseCell = ({ cell }) => <TextCell text={cell.value} />;
WarehouseCell.propTypes = {
  cell: PropTypes.shape({
    value: PropTypes.string,
  }).isRequired,
};

const GroupCell = ({ cell }) => <TextCell text={cell.value} />;
GroupCell.propTypes = {
  cell: PropTypes.shape({
    value: PropTypes.string,
  }).isRequired,
};

const PVMCell = ({ cell }) => <TextCell text={cell.value} />;
PVMCell.propTypes = {
  cell: PropTypes.shape({
    value: PropTypes.string,
  }).isRequired,
};

const StatusCell = ({ row }) => {
  const { t } = useLanguage();
  return (
    <Chip
      size="small"
      color={row.original.has_info ? "success" : "warning"}
      label={row.original.has_info ? t("configured") : t("missingSettings")}
      variant={row.original.has_info ? "outlined" : "filled"}
    />
  );
};
StatusCell.propTypes = {
  row: PropTypes.shape({ original: PropTypes.shape({ has_info: PropTypes.bool }) }).isRequired,
};

const XMLTypeCell = ({ cell }) => {
  const { t } = useLanguage();
  return <TextCell text={xmlTypeLabel(cell.value, t)} />;
};
XMLTypeCell.propTypes = {
  cell: PropTypes.shape({
    value: PropTypes.string,
  }).isRequired,
};

// Actions cell wrapper component with proper PropTypes
const ActionsCellWrapper = ({ row, handleEdit, handleDelete }) => (
  <ActionsCell row={row} handleEdit={handleEdit} handleDelete={handleDelete} />
);

ActionsCellWrapper.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    }).isRequired,
  }).isRequired,
  handleEdit: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
};

// Cell renderer component for Actions with proper PropTypes
const ActionsCellRenderer = ({ row }) => (
  <ActionsCell row={row} handleEdit={() => {}} handleDelete={() => {}} />
);

ActionsCellRenderer.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    }).isRequired,
  }).isRequired,
};

// Function that returns the table data structure
const supplierTableData = (handleEdit, handleDelete) => {
  // Create the Actions cell component with handlers bound
  const BoundActionsCellRenderer = ({ row }) => (
    <ActionsCell row={row} handleEdit={handleEdit} handleDelete={handleDelete} />
  );

  BoundActionsCellRenderer.propTypes = {
    row: PropTypes.shape({
      original: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      }).isRequired,
    }).isRequired,
  };

  return {
    columns: [
      {
        Header: "Pavadinimas",
        accessor: "name",
        Cell: NameCell,
      },
      {
        Header: "Registracijos Nr.",
        accessor: "supplier_registration",
        Cell: RegistrationCell,
      },
      {
        Header: "Būsena",
        accessor: "has_info",
        Cell: StatusCell,
      },
      {
        Header: "Pagrindinis Sandėlis",
        accessor: "primary_warehouse",
        Cell: WarehouseCell,
      },
      {
        Header: "Pagrindinė Grupė",
        accessor: "primary_group",
        Cell: GroupCell,
      },
      {
        Header: "PVM Kodas",
        accessor: "pvm_code",
        Cell: PVMCell,
      },
      {
        Header: "XML Tipas",
        accessor: "xml_type",
        Cell: XMLTypeCell,
      },
      {
        Header: "Veiksmai",
        accessor: "actions",
        Cell: BoundActionsCellRenderer,
      },
    ],
  };
};

export default supplierTableData;
